import React, { useCallback, useEffect, useState, useRef } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SkeletonItem } from '@atlaskit/menu';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import type { Props as FieldInlineEditProps } from '@atlassian/jira-issue-field-inline-edit/src/types.tsx';
import { FieldInlineEditStateLess } from '@atlassian/jira-issue-field-inline-edit/src/ui/index.tsx';
import { CONTENT_AREA } from '@atlassian/jira-issue-layout-common-constants/src/index.tsx';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import type { CmdbUiSource } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import {
	type AutoCompleteConfigType,
	type FieldId,
	type Messages,
	type RenderType,
	toString,
	type UseResolveFieldValuesByIdType,
	type ValueType,
	type UseResolveFieldValuesByIdResultType,
	type BasicServiceDetails,
} from '../../types/index.tsx';
import { useHoverState } from '../hover-state/index.tsx';
import { CollapsibleCard } from './card/index.tsx';
import { ActionButtons } from './edit/action-buttons/index.tsx';
import { CollapsibleFieldEdit } from './edit/main.tsx';
import { InlineErrorMessage } from './inline-error-message/index.tsx';
import { CollapsibleFieldLabel } from './label/index.tsx';
import { useCollapsibleField } from './use-collapsible-field/index.tsx';

const sortValues = <T,>(values: ValueType<T>[]): ValueType<T>[] =>
	[...values].sort((a, b) => a.name.localeCompare(b.name));

const displayValuesFromData = <T,>(
	data?: UseResolveFieldValuesByIdResultType<T>[],
): ValueType<T>[] => {
	// If we use `.filter()` instead of `.reduce()`, we'd need to disable the
	// type system. Using `.reduce()` allows Flow to statically analyse.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const errorsRemoved = (data ?? []).reduce<Array<any>>((acc, current) => {
		if (current instanceof Error) {
			return acc;
		}
		// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
		return [...acc, current] as const;
	}, []);

	return sortValues(errorsRemoved);
};

export type CollapsibleFieldProps<C, T> = {
	isDuplicatesAllowed: boolean;
	isExpanded?: boolean;
	isMultiple: boolean;
	actionSubject: string;
	source: CmdbUiSource;
	fieldId: FieldId;
	area: string;
	messages: Messages;
	render: RenderType<T>;
	useResolveFieldValuesById: UseResolveFieldValuesByIdType<T>;
	autoCompleteConfig: AutoCompleteConfigType<C, T>;
	initialServices?: ValueType<BasicServiceDetails>[];
} & FieldInlineEditProps & {
		onSuccess: () => void;
		onFailure: () => void;
		onChange: (value: ValueType<T>[] | null) => void;
	};

export const determineIsDirty = <T,>(
	initialValues: ValueType<T>[],
	currentValues: ValueType<T>[],
) => {
	if (initialValues.length !== currentValues.length) {
		return true;
	}

	const initialIds = initialValues.map((value) => value.id);
	const currentIds = currentValues.map((value) => value.id);

	return currentIds.reduce(
		(previous, current) => previous || initialIds.indexOf(current) === -1,
		false,
	);
};

/**
 * This component renders the entire field component section, including the
 * field heading and all the field cards. The field itself and the cards are
 * collapsable. The behaviour of the header and details can be customised by
 * injecting custom components to the `render` property. These custom components
 * will receive the current value. Additionally, tooltips for the buttons are
 * customisable as well, by using the `message` property.
 *
 * Some context around saving/persisting values to Bento:
 * - The Bento redux store provides two methods: `onChange` and `onConfirm`
 * - `onChange` should be used to 'stage' uncommited changes — nothing gets
 *   persisted by calling this
 * - `onConfirm` should only be called when we actually want to persist the
 *   changes
 * - Notice that `onConfirm` does not accept any field value. This call simply
 *   persists whatever was most recently staged with onChange
 */
export const CollapsibleField = <C, T>(props: CollapsibleFieldProps<C, T>) => {
	const {
		area,
		render,
		messages,
		isExpanded,
		isMultiple,
		onCancel,
		autoCompleteConfig,
		useResolveFieldValuesById,
		fieldId,
		actionSubject,
		source,
		onSuccess,
		onFailure,
		onEscape,
		isDuplicatesAllowed,
		onConfirm,
		onChange,
		initialServices,
	} = props;

	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [isFieldExpanded, setIsFieldExpanded] = useState<boolean>(isExpanded == null || isExpanded);

	const [displayValues, setDisplayValues] = useState<ValueType<T>[]>(
		// Type assertion is safe because BasicServiceDetails is a subset of ServiceDetails.
		// SSR provides BasicServiceDetails (id, name) which will be replaced with full
		// ServiceDetails (id, name, tier, description) when client data loads via useEffect.
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		fg('ssring_affected_services_in_issue_view') ? ((initialServices as ValueType<T>[]) ?? []) : [],
	);
	const [saveValues, setSaveValues] = useState<ValueType<T>[]>([]);
	const [isDirty, setIsDirty] = useState<boolean>(false);
	const [isHovered, hoverProps] = useHoverState();

	const issueKey = useIssueKey();
	const [{ value: fieldConfig }] = useFieldConfig(issueKey, toString(fieldId));
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { data: projectContext } = useResource(projectContextResource);
	const [{ isLoading, data, error }, { saveValue }] = useCollapsibleField({
		fieldId,
		onSuccess,
		onFailure,
		useResolveFieldValuesById,
	});

	const isProjectArchived = !!projectContext?.isProjectArchived;

	useEffect(() => {
		setIsDirty(determineIsDirty(saveValues, displayValues));
	}, [saveValues, displayValues]);

	const onExpandCollapseClick = useCallback(() => {
		setIsFieldExpanded(!isFieldExpanded);
	}, [isFieldExpanded]);

	const onEditModeClick = useCallback(() => {
		setSaveValues(displayValues);
		setIsFieldExpanded(true);
		setIsEditing(!isEditing);
	}, [displayValues, isEditing]);

	// This is called when the user 'confirms' the updated field values (by
	// clicking the tick icon shown under the field when in edit mode). This
	// method is responsible for 'committing' any staged changes to the backend.
	const onConfirmRequest = (analyticsEvent: UIAnalyticsEvent) => {
		setIsEditing(false);
		saveValue?.(displayValues, null, analyticsEvent).then(() => {
			const [to, from] = [
				displayValues.map((it) => it.id),
				displayValuesFromData(data).map((it) => it.id),
			];
			const [addedCount, removedCount] = [
				to.filter((toValue) => !from.includes(toValue)).length,
				from.filter((fromValue) => !to.includes(fromValue)).length,
			];
			fireTrackAnalytics(analyticsEvent, 'affectedServices updated', {
				addedCount,
				removedCount,
			});
		});

		onConfirm?.(analyticsEvent);
	};

	useEffect(() => {
		if (!fg('ssring_affected_services_in_issue_view') || (data && data.length > 0)) {
			setDisplayValues(displayValuesFromData(data));
		}
	}, [data]);

	const isViewTracked = useRef(false);
	useEffect(() => {
		if (!isLoading && !isViewTracked.current) {
			isViewTracked.current = true;
			const values = displayValuesFromData(data);
			const attributes = {
				objectIds: values.map((value) => ({
					id: value.id,
					isDemoService: value.name === 'Demo Service',
				})),
				numberOfObjects: values.length,
				// Distinguish between Insight vs Service views of this component
				source,
			};
			fireUIAnalytics(createAnalyticsEvent({}), 'insightField viewed', attributes);
		}
	}, [isLoading, data, source, createAnalyticsEvent]);

	const cancel = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			setIsEditing(false);
			setDisplayValues(saveValues);
			fireUIAnalytics(analyticsEvent);
		},
		[saveValues],
	);

	const onCancelRequest = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			cancel(analyticsEvent);
			onCancel?.(analyticsEvent);
		},
		[cancel, onCancel],
	);

	const onEditRequested = useCallback(() => {
		setSaveValues(displayValues);
	}, [displayValues]);

	const onEscapeRequest = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			cancel(analyticsEvent);
			onEscape && onEscape(analyticsEvent);
		},
		[cancel, onEscape],
	);

	// This gets called whenever the core select component gets a new value
	// added. Nothing is saved/persisted as a result of this method call.
	const onChangeRequest = (value: ValueType<T>) => {
		if (!isDuplicatesAllowed && displayValues.some((x: ValueType<T>) => x.id === value.id)) {
			return;
		}

		const values = sortValues([...displayValues, value]);
		setDisplayValues(values);

		onChange?.(values);
	};

	// This gets called whenever a cards' delete button is clicked. Nothing is
	// saved/persisted as a result of this method call.
	const onHandleDelete = (value: ValueType<T>) => {
		const values = displayValues.filter((x: ValueType<T>) => x.id !== value.id);
		setDisplayValues(values);

		// There is currently an issue with passing an empty array to the
		// onChange callback — it seems to get filtered out. Passing null
		// here instead of an empty array as a workaround in those cases.
		onChange?.(values.length > 0 ? values : null);
	};

	// Left empty as this component has it's own special read view
	const renderReadView = () => null;

	const renderEditView = () => (
		<CollapsibleFieldEdit
			isMultiple={isMultiple}
			values={displayValues}
			onChange={onChangeRequest}
			autoCompleteConfig={autoCompleteConfig}
			OptionContent={render.OptionContent}
			messages={messages}
		/>
	);

	if (!fieldConfig) {
		return null;
	}

	return (
		<Box
			onMouseOver={!isEditing ? hoverProps.onMouseOver : undefined}
			onFocus={!isEditing ? hoverProps.onFocus : undefined}
			onMouseOut={!isEditing ? hoverProps.onMouseOut : undefined}
			onBlur={!isEditing ? hoverProps.onBlur : undefined}
			xcss={[wrapperStyles, area === CONTENT_AREA && wrapperAreaStyles]}
		>
			<CollapsibleFieldLabel
				isEditing={isEditing}
				canEdit={!isProjectArchived}
				isLoading={isLoading}
				onEditModeClick={onEditModeClick}
				values={displayValues}
				isParentHovered={isEditing || isHovered}
				isExpanded={isFieldExpanded}
				onExpandCollapseClick={onExpandCollapseClick}
				inlineErrorMessage={
					error ? <InlineErrorMessage messageDescriptor={messages.globalErrorMessage} /> : undefined
				}
				messages={messages}
				additionalLabelContent={render.AdditionalLabelContent}
				issueKey={issueKey}
				fieldId={toString(fieldId)}
			>
				{fieldConfig.title}
			</CollapsibleFieldLabel>
			{isLoading && !fg('ssring_affected_services_in_issue_view') && (
				<UFOLoadHold name="service-entity-collapsible-field-loading">
					<Box xcss={cardsWrapperFirstStyles}>
						<SkeletonItem isShimmering />
					</Box>
				</UFOLoadHold>
			)}
			{isFieldExpanded &&
				(!isLoading || fg('ssring_affected_services_in_issue_view')) &&
				displayValues.length > 0 && (
					<>
						{displayValues.map((value, index) => (
							<Box
								key={`${value.id}-${index}`}
								xcss={[
									index === 0 && cardsWrapperFirstStyles,
									index === displayValues.length - 1 && cardsWrapperLastStyles,
								]}
							>
								<CollapsibleCard
									value={value}
									isEditing={isEditing}
									render={render}
									onHandleDelete={onHandleDelete}
									messages={messages}
								/>
							</Box>
						))}
					</>
				)}
			{isEditing && (
				<>
					<Box xcss={fieldContainerStyles}>
						<FieldInlineEditStateLess
							actionSubject={actionSubject}
							label={fieldConfig.title}
							fieldId={fieldId}
							isEditable={fieldConfig.isEditable}
							isEditing={isEditing}
							isLabelHidden
							readView={renderReadView()}
							editView={fieldConfig.isEditable ? renderEditView() : null}
							onCancel={onCancelRequest}
							onConfirm={onConfirmRequest}
							onEdit={onEditRequested}
							onEscape={onEscapeRequest}
							isFitContainerWidthReadView
							areActionButtonsHidden
							isConfirmOnBlurDisabled
							testId="issue-field-service-entity.common.ui.collapsible-field.inline-edit-field"
						/>
					</Box>
					<Box xcss={actionButtonWrapperStyles}>
						<ActionButtons
							isConfirmDisabled={!isDirty}
							onConfirm={onConfirmRequest}
							onCancel={onCancelRequest}
						/>
					</Box>
				</>
			)}
		</Box>
	);
};

const wrapperStyles = xcss({
	position: 'relative',
	marginBottom: 'space.200',
});

const wrapperAreaStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	borderRadius: 'radius.xsmall',
	paddingTop: 'space.100',
	paddingRight: 'space.200',
});

const fieldContainerStyles = xcss({
	marginTop: 'space.150',
});

const cardsWrapperFirstStyles = xcss({
	marginTop: 'space.150',
});

const cardsWrapperLastStyles = xcss({
	marginBottom: 'space.100',
});

const actionButtonWrapperStyles = xcss({
	position: 'absolute',
	top: `calc(100% + ${token('space.100')})`,
	right: token('space.0'),
	zIndex: 'navigation',
});
